/**
 * Client-side counterpart of App\Services\FingerprintService. Used only to throttle the
 * same error inside one session (a render loop must not send 1000 events); the server
 * computes the authoritative fingerprint for grouping.
 */
export function normalizeUrl(url: string): string {
  if (!url) return '';
  let path = url;
  try {
    const u = new URL(url, typeof location !== 'undefined' ? location.href : 'http://localhost');
    path = u.pathname;
  } catch {
    path = url.replace(/[?#].*$/, '');
    if (!path.startsWith('/')) path = '/' + path;
  }
  const segments = path.split('/').map((s) => {
    if (!s) return s;
    if (/^\d+$/.test(s)) return ':id';
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return ':id';
    if (/^[0-9a-f]{16,}$/i.test(s)) return ':id';
    if (s.length >= 12 && /\d/.test(s) && /^[\w.-]+$/.test(s)) return ':id';
    return s;
  });
  const out = segments.join('/').replace(/\/+$/, '');
  return out === '' ? '/' : out;
}

export function fingerprint(method: string | undefined, url: string | undefined, status: number | undefined, message: string | undefined): string {
  const parts = [
    (method ?? '').toUpperCase().trim(),
    normalizeUrl(url ?? ''),
    status === undefined || status === null ? '' : String(status),
    (message ?? '').replace(/\s+/g, ' ').trim().slice(0, 100),
  ];
  return hash(parts.join('\n'));
}

/** Small non-cryptographic hash (FNV-1a, 32-bit) — enough for in-session de-duplication. */
export function hash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Allows at most `max` occurrences of a fingerprint per `windowMs`. */
export class Throttle {
  private seen = new Map<string, number[]>();

  constructor(private readonly max = 5, private readonly windowMs = 60_000) {}

  allow(key: string, at = Date.now()): boolean {
    const times = (this.seen.get(key) ?? []).filter((t) => at - t < this.windowMs);
    if (times.length >= this.max) {
      this.seen.set(key, times);
      return false;
    }
    times.push(at);
    this.seen.set(key, times);
    return true;
  }
}
